import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { PageHeader, MockBadge, StatusBadge, Spinner } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { RotateCcw, Download } from "lucide-react";

export default function Admin() {
  const [settings, setSettings] = useState(null);
  const [users, setUsers] = useState([]);
  const [cfg, setCfg] = useState(null);

  const load = () => { api.get("/integrations").then((r) => setSettings(r.data)); api.get("/admin/users").then((r) => setUsers(r.data)); api.get("/config/all").then((r) => setCfg(r.data)); };
  useEffect(() => { load(); }, []);
  if (!settings || !cfg) return <Spinner />;

  const setMode = async (adapter, mode) => { const r = await api.put(`/integrations/${adapter}`, { mode }); setSettings({ ...settings, adapters: r.data.adapters }); toast.success(`${adapter} → ${mode}`); };
  const reset = async () => {
    if (!window.confirm("Reset all demo data to seed state? This clears records and audit history.")) return;
    try { await api.post("/admin/reset-demo", { confirm: "RESET" }); toast.success("Demo data reset"); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const downloadEvidence = async () => {
    try {
      const r = await api.get("/deliverables/verification-report.zip", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "barjeel-verification-evidence.zip"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Verification evidence downloaded");
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader overline="Platform" title="Administration & Integrations"
        subtitle="Manage integration adapters, review configuration and reset demo data." testid="admin-header"
        actions={<div className="flex gap-2">
          <Button variant="outline" className="gap-1" onClick={downloadEvidence} data-testid="download-verification-evidence-btn"><Download className="h-4 w-4" /> Verification evidence</Button>
          <Button variant="outline" className="gap-1" onClick={reset} data-testid="reset-demo-btn"><RotateCcw className="h-4 w-4" /> Reset demo data</Button>
        </div>} />

      <Tabs defaultValue="integrations">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="integrations" data-testid="admin-tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="config" data-testid="admin-tab-config">Configuration</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users & Roles</TabsTrigger>
        </TabsList>

        <TabsContent value="integrations" className="mt-4">
          <Card className="p-5">
            <p className="text-sm text-muted-foreground mb-4">{settings.note}</p>
            <div className="space-y-3">
              {Object.entries(settings.adapters).map(([key, a]) => (
                <div key={key} className="flex items-center justify-between border border-border rounded-lg p-4" data-testid={`adapter-${key}`}>
                  <div>
                    <div className="font-medium flex items-center gap-2">{a.name} {a.mode === "mock" && <MockBadge />}{a.mode === "live" && <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">LIVE</span>}</div>
                    <div className="text-xs text-muted-foreground">Provider status: {a.status.replace(/_/g, " ")}</div>
                  </div>
                  <Select value={a.mode} onValueChange={(v) => setMode(key, v)}>
                    <SelectTrigger className="w-32 h-9" data-testid={`adapter-mode-${key}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="mock">Mock</SelectItem><SelectItem value="live">Live</SelectItem><SelectItem value="unavailable">Unavailable</SelectItem></SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-amber-700 mt-4">In "Live" mode an adapter will NOT report a successful verification unless a real provider is configured and returns a genuine response — with no provider wired it returns "unavailable". Real connectivity requires provider credentials, access and authorisation (see assumptions & dependencies).</p>
          </Card>
        </TabsContent>

        <TabsContent value="config" className="mt-4 space-y-4">
          <Card className="p-5">
            <h3 className="font-semibold mb-1">Document Requirements <span className="text-xs text-amber-700 font-normal">· {cfg.doc_requirements.label}</span></h3>
            {Object.entries(cfg.doc_requirements.requirements).map(([k, v]) => (
              <div key={k} className="text-sm py-1"><span className="font-medium capitalize">{k.replace(/_/g, " ")}:</span> <span className="text-muted-foreground">{v.join(", ")}</span></div>
            ))}
          </Card>
          <Card className="p-5">
            <h3 className="font-semibold mb-1">Transaction Types <span className="text-xs text-amber-700 font-normal">· {cfg.transaction_types.label}</span></h3>
            {cfg.transaction_types.types.map((t) => <div key={t.code} className="text-sm py-1">{t.name} — approval {t.requires_approval ? "required" : "not required"}, KYC {t.requires_kyc ? "required" : "not required"}</div>)}
          </Card>
          <Card className="p-5">
            <h3 className="font-semibold mb-2">FX Rates (dated mock) · {cfg.fx_rates[0]?.date}</h3>
            <Table><TableHeader><TableRow><TableHead>Currency</TableHead><TableHead>Per USD</TableHead></TableRow></TableHeader>
              <TableBody>{cfg.fx_rates.map((f) => <TableRow key={f.base}><TableCell className="font-num">{f.base}</TableCell><TableCell className="font-num">{f.rate}</TableCell></TableRow>)}</TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <Card className="p-5">
            <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead></TableRow></TableHeader>
              <TableBody>{users.map((u) => (
                <TableRow key={u.id} data-testid={`user-row-${u.role}`}><TableCell className="font-medium">{u.name}</TableCell><TableCell className="text-xs">{u.email}</TableCell>
                  <TableCell><StatusBadge status={u.role} /></TableCell></TableRow>
              ))}</TableBody>
            </Table>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
}
